import React, { useState, useEffect } from 'react';
import { getAnnouncements, createAnnouncement, updateAnnouncement, deleteAnnouncement } from './adminApi';
import './Announcements.css';

const Announcements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, page_size: 20, total: 0 });
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [currentItem, setCurrentItem] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'info',
    is_active: true,
    expires_at: ''
  });

  useEffect(() => {
    fetchAnnouncements();
  }, [pagination.page]);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const result = await getAnnouncements({ page: pagination.page, page_size: pagination.page_size });
      setAnnouncements(result.items);
      setPagination(prev => ({ ...prev, total: result.total }));
    } catch (err) {
      console.error('获取公告列表失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setModalMode('create');
    setFormData({ title: '', content: '', type: 'info', is_active: true, expires_at: '' });
    setCurrentItem(null);
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setModalMode('edit');
    setCurrentItem(item);
    setFormData({
      title: item.title,
      content: item.content,
      type: item.type,
      is_active: item.is_active,
      expires_at: item.expires_at ? item.expires_at.slice(0, 16) : ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setCurrentItem(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        expires_at: formData.expires_at ? new Date(formData.expires_at).toISOString() : null
      };
      
      if (modalMode === 'create') {
        await createAnnouncement(data);
      } else {
        await updateAnnouncement(currentItem.id, data);
      }
      closeModal();
      fetchAnnouncements();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`确定要删除公告 "${item.title}" 吗？`)) return;
    try {
      await deleteAnnouncement(item.id);
      fetchAnnouncements();
    } catch (err) {
      alert(err.message);
    }
  };

  const toggleActive = async (item) => {
    try {
      await updateAnnouncement(item.id, { is_active: !item.is_active });
      fetchAnnouncements();
    } catch (err) {
      alert(err.message);
    }
  };

  const getTypeLabel = (type) => {
    const labels = { info: '信息', warning: '警告', error: '错误' };
    return labels[type] || type;
  };

  const totalPages = Math.ceil(pagination.total / pagination.page_size);

  return (
    <div className="announcements-page">
      <div className="page-header">
        <h2 className="page-title">公告管理</h2>
        <button className="admin-btn admin-btn-primary" onClick={openCreateModal}>
          + 发布公告
        </button>
      </div>

      {/* 公告列表 */}
      <div className="admin-card">
        {loading ? (
          <div className="admin-loading"><div className="admin-loading-spinner"></div></div>
        ) : (
          <>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>标题</th>
                  <th>类型</th>
                  <th>状态</th>
                  <th>创建时间</th>
                  <th>过期时间</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {announcements.map(item => (
                  <tr key={item.id}>
                    <td>{item.id}</td>
                    <td>{item.title}</td>
                    <td>
                      <span className={`admin-tag admin-tag-${item.type === 'warning' ? 'warning' : item.type === 'error' ? 'error' : 'info'}`}>
                        {getTypeLabel(item.type)}
                      </span>
                    </td>
                    <td>
                      <span className={`admin-tag ${item.is_active ? 'admin-tag-success' : 'admin-tag-error'}`}>
                        {item.is_active ? '已发布' : '已禁用'}
                      </span>
                    </td>
                    <td>{item.created_at}</td>
                    <td>{item.expires_at || '-'}</td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className={`admin-btn admin-btn-sm ${item.is_active ? 'admin-btn-default' : 'admin-btn-primary'}`}
                          onClick={() => toggleActive(item)}
                        >
                          {item.is_active ? '禁用' : '启用'}
                        </button>
                        <button className="admin-btn admin-btn-sm admin-btn-default" onClick={() => openEditModal(item)}>
                          编辑
                        </button>
                        <button className="admin-btn admin-btn-sm admin-btn-danger" onClick={() => handleDelete(item)}>
                          删除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {announcements.length === 0 && (
                  <tr>
                    <td colSpan="7" className="admin-empty">暂无公告</td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* 分页 */}
            {totalPages > 1 && (
              <div className="admin-pagination">
                <button 
                  disabled={pagination.page === 1}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                >
                  上一页
                </button>
                <span>第 {pagination.page} / {totalPages} 页</span>
                <button 
                  disabled={pagination.page === totalPages}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                >
                  下一页
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* 模态框 */}
      {showModal && (
        <div className="admin-modal-overlay" onClick={closeModal}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3 className="admin-modal-title">
                {modalMode === 'create' ? '发布公告' : '编辑公告'}
              </h3>
              <button className="admin-modal-close" onClick={closeModal}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="admin-modal-body">
                <div className="admin-form-group">
                  <label className="admin-form-label">标题</label>
                  <input
                    type="text"
                    className="admin-input"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label">内容</label>
                  <textarea
                    className="admin-input"
                    rows={4}
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    required
                  />
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label">类型</label>
                  <select
                    className="admin-select"
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  >
                    <option value="info">信息</option>
                    <option value="warning">警告</option>
                    <option value="error">错误</option>
                  </select>
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label">过期时间（可选）</label>
                  <input
                    type="datetime-local"
                    className="admin-input"
                    value={formData.expires_at}
                    onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                  />
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                    立即发布
                  </label>
                </div>
              </div>
              <div className="admin-modal-footer">
                <button type="button" className="admin-btn admin-btn-default" onClick={closeModal}>
                  取消
                </button>
                <button type="submit" className="admin-btn admin-btn-primary">
                  {modalMode === 'create' ? '发布' : '保存'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Announcements;